import { useCallback, useEffect, useMemo, useState } from "react";
import { ChartColumnIncreasing } from "lucide-react";
import { formatDateTimeBR } from "@tracker-bet-analystic/domain";
import { useAuth } from "../context/AuthContext";
import { describeApiError, fetchCoverage, probeFootballData, updateCoverage, type CoverageMetricPayload, type CoverageRowPayload, type FootballDataProbePayload } from "../lib/analysis-api";
import { EmptyState } from "../components/EmptyState";
import {
  COVERAGE_TABLE_METRICS as TABLE_METRICS,
  canUpdateCoverage,
  coverageLevelClass,
  coverageLevelLabel,
  formatCoveragePercent,
} from "./coverage-view";

/**
 * P01 — cobertura real dos dados do provedor por competição.
 * Mede disponibilidade de dados (não é recomendação de aposta) e respeita o
 * threshold de 80% do motor. Somente leitura para membros; atualização só owner.
 */

function MetricCell({ metric }: { metric: CoverageMetricPayload | undefined }) {
  if (!metric) return <td className="text-[var(--color-text-muted)]">—</td>;
  return (
    <td>
      <span className={`badge ${coverageLevelClass(metric.level)}`}>{coverageLevelLabel(metric.level)}</span>
      <span className="block text-xs text-[var(--color-text-muted)] mt-1 tabular-nums">
        {formatCoveragePercent(metric.coverage)} ({metric.minValid}/{metric.games})
      </span>
    </td>
  );
}

export function CoverageScreen() {
  const { identity } = useAuth();
  const isOwner = canUpdateCoverage(identity?.role);

  const [rows, setRows] = useState<CoverageRowPayload[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [footballProbe, setFootballProbe] = useState<FootballDataProbePayload | null>(null);
  const [probingFootballData, setProbingFootballData] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchCoverage();
    setLoading(false);
    if (!result.ok) {
      setError(describeApiError(result));
      return;
    }
    setError(null);
    setRows(result.data.rows ?? []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const lastCalculated = useMemo(() => {
    const dates = rows.map((row) => row.calculated_at).filter(Boolean).sort();
    return dates.length > 0 ? dates[dates.length - 1] : null;
  }, [rows]);

  const runUpdate = async () => {
    if (running) return;
    setRunning(true);
    setNotice(null);
    const result = await updateCoverage(false);
    setRunning(false);
    if (!result.ok) {
      setError(describeApiError(result));
      return;
    }
    const data = result.data;
    if (data.busy) {
      setNotice("Já existe uma atualização de cobertura em andamento. Aguarde alguns minutos.");
      return;
    }
    if (data.reused) {
      setNotice(`Diagnóstico recente reaproveitado (${formatDateTimeBR(data.calculatedAt)}) — nenhuma chamada externa feita.`);
    } else {
      setNotice(
        `Cobertura atualizada: ${data.totals.analyzedGames} jogos analisados, ` +
          `${data.totals.providerCalls} chamadas externas, ${data.totals.cacheHits} cache hits, ` +
          `${data.totals.cacheMisses} cache misses, ${data.totals.newGames} jogos novos baixados.`
      );
    }
    await load();
  };

  const runFootballDataProbe = async () => {
    if (probingFootballData || !isOwner) return;
    setProbingFootballData(true);
    setFootballProbe(null);
    setError(null);
    const result = await probeFootballData();
    setProbingFootballData(false);
    if (!result.ok) {
      setError(describeApiError(result));
      return;
    }
    setFootballProbe(result.data);
  };

  return (
    <div>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Diagnóstico</p>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Cobertura das competições</h1>
          <p className="text-[var(--color-text-muted)] mt-1">
            Disponibilidade real de estatísticas do GoalDir por competição. Mede apenas qualidade de dados —
            não é recomendação de aposta. O motor exige 80% de cobertura em ambos os lados.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void runUpdate()}
          disabled={running || !isOwner}
          title={isOwner ? "Pode consumir chamadas externas ao provedor" : "Somente o proprietário do espaço"}
        >
          {running ? "Atualizando cobertura…" : "Atualizar cobertura"}
        </button>
      </header>

      <div className="alert alert-info mb-6" role="status">
        A atualização é cache-first: reaproveita fixtures, estatísticas e cache já existentes e só consulta o provedor
        quando falta dado. Pode consumir chamadas externas. Um diagnóstico com menos de 15 minutos é reaproveitado.
      </div>

      {isOwner && (
        <section className="card p-5 mb-6" aria-labelledby="football-data-probe-title">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 id="football-data-probe-title" className="font-semibold">Teste da FootballData.io</h2>
              <p className="text-sm text-[var(--color-text-muted)] mt-1">
                Consulta cobertura, agenda, estatísticas e cotações. Pode consumir até cinco chamadas da sua cota gratuita.
              </p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => void runFootballDataProbe()} disabled={probingFootballData}>
              {probingFootballData ? "Consultando API…" : "Testar conexão"}
            </button>
          </div>
          {footballProbe && (
            <div className="mt-4 space-y-2 text-sm" role="status" aria-live="polite">
              {!footballProbe.configured ? (
                <p className="alert alert-warning">O segredo FOOTBALLDATA_API_KEY não está configurado no servidor.</p>
              ) : (
                <>
                  <p><strong>Cobertura:</strong> {footballProbe.coverage?.success ? "respondendo" : `erro HTTP ${footballProbe.coverage?.httpStatus ?? "—"}`}
                    {footballProbe.coverage?.plan ? ` · plano ${footballProbe.coverage.plan}` : ""}
                    {footballProbe.coverage?.requestsUsed !== null && footballProbe.coverage?.requestsUsed !== undefined
                      ? ` · ${footballProbe.coverage.requestsUsed}${footballProbe.coverage.requestsLimit !== null && footballProbe.coverage.requestsLimit !== undefined ? `/${footballProbe.coverage.requestsLimit}` : ""} chamadas usadas`
                      : ""}
                  </p>
                  <p><strong>Agenda:</strong> {footballProbe.fixtures?.success ? `${footballProbe.fixtures.count} partida(s) retornada(s)` : `erro HTTP ${footballProbe.fixtures?.httpStatus ?? "—"}${footballProbe.fixtures?.errorCode ? ` · ${footballProbe.fixtures.errorCode}` : ""}`}</p>
                  <p><strong>Estatísticas:</strong> {footballProbe.statistics?.success ? `disponíveis · ${footballProbe.statistics.fields.join(", ") || "sem campos identificados"}` : footballProbe.statistics ? `indisponíveis (HTTP ${footballProbe.statistics.httpStatus}${footballProbe.statistics.errorCode ? ` · ${footballProbe.statistics.errorCode}` : ""})` : "sem partida para testar"}</p>
                  <p><strong>Cotações:</strong> {footballProbe.odds?.success ? `disponíveis · ${footballProbe.odds.fields.join(", ") || "sem campos identificados"}` : footballProbe.odds ? `indisponíveis (HTTP ${footballProbe.odds.httpStatus}${footballProbe.odds.errorCode ? ` · ${footballProbe.odds.errorCode}` : ""})` : "sem partida para testar"}</p>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {notice && <div className="alert alert-info mb-4" role="status">{notice}</div>}
      {error && <div className="alert alert-warning mb-4" role="alert">{error}</div>}

      {!isOwner && (
        <div className="alert alert-warning mb-4" role="alert">
          Seu perfil não é proprietário do espaço: você pode consultar a cobertura, mas não atualizar.
        </div>
      )}

      {loading ? (
        <p className="text-[var(--color-text-muted)]">Carregando diagnóstico…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ChartColumnIncreasing />}
          title="Nenhum diagnóstico registrado"
          text="Use “Atualizar cobertura” para medir a cobertura real das competições prioritárias."
        />
      ) : (
        <>
          <div className="card overflow-hidden mb-6">
            <div className="table-container">
              <table className="table" role="table">
                <thead>
                  <tr>
                    <th scope="col">Competição</th>
                    <th scope="col">Jogos</th>
                    {TABLE_METRICS.map((metric) => (
                      <th key={metric.key} scope="col">{metric.label}</th>
                    ))}
                    <th scope="col">Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.competition_name}-${row.competition_external_id ?? ""}`}>
                      <td className="font-medium">
                        {row.competition_name}
                        {row.country ? <span className="block text-xs text-[var(--color-text-muted)]">{row.country}</span> : null}
                      </td>
                      <td className="tabular-nums">
                        {row.sample_size}
                        <span className="block text-xs text-[var(--color-text-muted)]">alvo {row.sample_target}</span>
                      </td>
                      {TABLE_METRICS.map((metric) => (
                        <MetricCell key={metric.key} metric={row.metrics?.[metric.key]} />
                      ))}
                      <td className="text-sm text-[var(--color-text-muted)]">{formatDateTimeBR(row.calculated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <section className="card p-5">
              <h2 className="font-semibold mb-2">Mercados com dados suficientes (≥ 80%)</h2>
              {(() => {
                const withData = [...new Set(rows.flatMap((row) => row.markets?.withData ?? []))];
                return withData.length > 0 ? (
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {withData.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-[var(--color-text-muted)]">
                    Nenhuma métrica atingiu 80% em ambos os lados nas competições medidas.
                  </p>
                );
              })()}
            </section>
            <section className="card p-5">
              <h2 className="font-semibold mb-2">Mercados limitados pela cobertura</h2>
              {(() => {
                const limited = [...new Set(rows.flatMap((row) => row.markets?.limited ?? []))];
                return limited.length > 0 ? (
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {limited.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-[var(--color-text-muted)]">Nenhuma limitação registrada.</p>
                );
              })()}
            </section>
          </div>

          <section className="card p-5 mt-4">
            <h2 className="font-semibold mb-2">Contabilidade do último diagnóstico</h2>
            <div className="grid gap-3 sm:grid-cols-4 text-sm">
              <div>
                <p className="text-[var(--color-text-muted)]">Chamadas externas</p>
                <p className="text-xl font-bold tabular-nums">{rows.reduce((sum, row) => sum + (row.provider_calls ?? 0), 0)}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)]">Cache hits</p>
                <p className="text-xl font-bold tabular-nums">{rows.reduce((sum, row) => sum + (row.cache_hits ?? 0), 0)}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)]">Cache misses</p>
                <p className="text-xl font-bold tabular-nums">{rows.reduce((sum, row) => sum + (row.cache_misses ?? 0), 0)}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)]">Jogos novos baixados</p>
                <p className="text-xl font-bold tabular-nums">{rows.reduce((sum, row) => sum + (row.new_games ?? 0), 0)}</p>
              </div>
            </div>
            <p className="text-xs text-[var(--color-text-muted)] mt-3">
              Última atualização: {lastCalculated ? formatDateTimeBR(lastCalculated) : "—"}. Cobertura não é promessa de
              resultado: frequência histórica não é probabilidade calibrada.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
